import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Avatar } from '../components/common/Avatar';
import { CameraIcon, SaveIcon, UserIcon } from '../components/icons/Icons';
import { AppSectionHero, AppStatCard } from '../components/common/AppSurface';
import '../components/common/SharedInputs.css';
import './MyProfilePage.css';

export const MyProfilePage: React.FC = () => {
  const {
    currentUser,
    updateCurrentUserProfile,
    updateCurrentUserAvatar,
    removeCurrentUserAvatar,
    updatePassword,
  } = useAuth();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [displayName, setDisplayName] = useState(currentUser?.name || '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    setDisplayName(currentUser?.name || '');
  }, [currentUser?.name]);

  if (!currentUser) return null;

  const handleSaveProfile = async () => {
    setProfileError('');
    setProfileSaving(true);

    const result = await updateCurrentUserProfile({ name: displayName });
    setProfileSaving(false);

    if (!result.success) {
      setProfileError(result.error || 'Unable to save profile.');
      showToast(result.error || 'Unable to save profile.', 'error');
      return;
    }

    showToast('Profile updated successfully', 'success');
  };

  const handleAvatarPick = () => fileInputRef.current?.click();

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setAvatarSaving(true);
    const result = await updateCurrentUserAvatar(file);
    setAvatarSaving(false);

    if (!result.success) {
      showToast(result.error || 'Unable to upload profile photo.', 'error');
      return;
    }

    showToast('Profile photo updated', 'success');
  };

  const handleRemoveAvatar = async () => {
    setAvatarSaving(true);
    const result = await removeCurrentUserAvatar();
    setAvatarSaving(false);

    if (!result.success) {
      showToast(result.error || 'Unable to remove profile photo.', 'error');
      return;
    }

    showToast('Profile photo removed', 'info');
  };

  const handleSavePassword = async () => {
    setPasswordError('');

    if (!newPassword || newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    setPasswordSaving(true);
    const result = await updatePassword(newPassword);
    setPasswordSaving(false);

    if (!result.success) {
      setPasswordError(result.error || 'Unable to update password.');
      showToast(result.error || 'Unable to update password.', 'error');
      return;
    }

    setNewPassword('');
    setConfirmPassword('');
    showToast('Password updated successfully', 'success');
  };

  return (
    <div className="profile-page">
      <AppSectionHero
        icon={<UserIcon size={28} />}
        title="My Profile"
        subtitle="Update your name, profile photo, and account password."
        aside={
          <>
            <AppStatCard icon={<UserIcon size={20} />} label="Role" value={currentUser.role.replace(/_/g, ' ')} caption="Current access level" tone="blue" />
            <AppStatCard icon={<CameraIcon size={20} />} label="Photo" value={currentUser.avatarUrl ? 'Set' : 'Default'} caption="Visible across the app" tone="mint" />
          </>
        }
      />

      <div className="profile-grid">
        <section className="profile-card profile-identity-card">
          <div className="profile-avatar-panel">
            <Avatar user={currentUser} size={128} className="profile-avatar-xl" />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleAvatarChange}
            />
            <div className="profile-avatar-actions">
              <button
                type="button"
                className="profile-action-btn primary"
                onClick={handleAvatarPick}
                disabled={avatarSaving}
              >
                <CameraIcon size={16} />
                {avatarSaving ? 'Uploading...' : 'Change Photo'}
              </button>
              {currentUser.avatarUrl && (
                <button
                  type="button"
                  className="profile-action-btn subtle"
                  onClick={handleRemoveAvatar}
                  disabled={avatarSaving}
                >
                  Remove Photo
                </button>
              )}
            </div>
          </div>

          <div className="profile-identity-meta">
            <span className="profile-role-badge">{currentUser.role.replace(/_/g, ' ')}</span>
            <h2>{currentUser.name}</h2>
            <p>{currentUser.email}</p>
          </div>
        </section>

        <div className="profile-column">
        <section className="profile-card">
          <div className="profile-card-head">
            <h3>Personal Information</h3>
            <p>Your display name is visible across tasks, comments, and team views.</p>
          </div>

          <div className="profile-form">
            <label className="profile-field">
              <span>Name</span>
              <input
                className="inline-input"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="Your full name"
              />
            </label>

            <label className="profile-field">
              <span>Email</span>
              <input className="inline-input profile-readonly" value={currentUser.email} disabled />
            </label>

            <label className="profile-field">
              <span>Role</span>
              <input className="inline-input profile-readonly" value={currentUser.role.replace(/_/g, ' ')} disabled />
            </label>

            {profileError && <p className="profile-error">{profileError}</p>}

            <button
              type="button"
              className="profile-save-btn"
              onClick={handleSaveProfile}
              disabled={profileSaving}
            >
              <SaveIcon />
              {profileSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </section>

        <section className="profile-card profile-security-card">
        <div className="profile-card-head">
          <h3>Password & Security</h3>
          <p>Choose a strong password with at least 8 characters.</p>
        </div>

        <div className="profile-security-grid">
          <label className="profile-field">
            <span>New Password</span>
            <input
              type="password"
              className="inline-input"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Enter new password"
            />
          </label>

          <label className="profile-field">
            <span>Confirm Password</span>
            <input
              type="password"
              className="inline-input"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
            />
          </label>
        </div>

        {passwordError && <p className="profile-error">{passwordError}</p>}

        <button
          type="button"
          className="profile-save-btn"
          onClick={handleSavePassword}
          disabled={passwordSaving}
        >
          <SaveIcon />
          {passwordSaving ? 'Updating...' : 'Update Password'}
        </button>
      </section>
        </div>
      </div>
    </div>
  );
};
